import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, GraduationCap, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export function PublicHeader() {
    const [open, setOpen] = useState(false);
    const { user } = useAuth();
    const { t } = useTranslation();

    const NAV = [
        { to: "/", label: t("nav.home") },
        { to: "/funktionen", label: t("nav.features") },
        { to: "/preise", label: t("nav.pricing") },
        { to: "/ueber-uns", label: t("nav.about") },
        { to: "/faq", label: t("nav.faq") },
        { to: "/kontakt", label: t("nav.contact") },
    ];

    return (
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-100" data-testid="public-header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-3">
                <Link to="/" data-testid="logo-link" className="relative flex items-center gap-2 shrink-0">
                    <span className="b24-logo-shine"><img src="/logo-icon.png" alt="bewerbung.ai" className="h-10 w-auto object-contain" /></span>
                    <span className="font-heading font-bold text-lg tracking-tight hidden sm:inline"><span className="b24-shine-dark">Bewerbung24</span><span className="b24-shine-red">.ai</span></span>
                    <span data-testid="beta-badge" className="absolute -top-2.5 left-1/2 -translate-x-1/2 -rotate-6 px-2 py-px rounded-sm text-[9px] font-extrabold uppercase tracking-[0.3em] bg-amber-400 text-white shadow-md pointer-events-none select-none">Beta</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-0.5">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-link-${item.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                                    isActive ? "text-zinc-950 bg-zinc-100" : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-50"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    <Link
                        to="/partner"
                        data-testid="header-partner-link"
                        className="group inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold text-zinc-900 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors whitespace-nowrap"
                    >
                        <GraduationCap size={14} />
                        {t("nav.forPartners", "Für Partner")}
                        <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                    <LanguageSwitcher />
                    {user && typeof user === "object" ? (
                        <Link to="/dashboard" data-testid="header-dashboard-button" className="px-5 py-2.5 rounded-full font-semibold text-sm btn-primary-grad">
                            {t("nav.dashboard")}
                        </Link>
                    ) : (
                        <>
                            <Link to="/login" data-testid="header-login-link" className="px-4 py-2 text-sm font-medium text-zinc-700 hover:text-zinc-950">
                                {t("nav.login")}
                            </Link>
                            <Link to="/registrieren" data-testid="header-register-button" className="px-5 py-2.5 rounded-full font-semibold text-sm btn-primary-grad">
                                {t("nav.register")}
                            </Link>
                        </>
                    )}
                </div>

                <div className="lg:hidden flex items-center gap-2">
                    <LanguageSwitcher compact />
                    <button
                        className="p-2"
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        aria-label="Menü"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-zinc-100 bg-white" data-testid="mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-2">
                        {NAV.map((item) => (
                            <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="py-2 text-zinc-700 font-medium">
                                {item.label}
                            </Link>
                        ))}
                        <Link
                            to="/partner"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-partner-link"
                            className="my-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-zinc-900 font-semibold inline-flex items-center gap-2"
                        >
                            <GraduationCap size={16} /> {t("nav.forPartners", "Für Partner")}
                        </Link>
                        <div className="border-t border-zinc-100 pt-3 mt-2 flex flex-col gap-2">
                            {user && typeof user === "object" ? (
                                <Link to="/dashboard" onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full font-semibold text-sm btn-primary-grad text-center">
                                    {t("nav.dashboard")}
                                </Link>
                            ) : (
                                <>
                                    <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-zinc-700 font-medium">
                                        {t("nav.login")}
                                    </Link>
                                    <Link to="/registrieren" onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full font-semibold text-sm btn-primary-grad text-center">
                                        {t("nav.register")}
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

export function PublicFooter() {
    const { t } = useTranslation();
    return (
        <footer className="bg-warm border-t border-zinc-100 mt-24" data-testid="public-footer">
            {/* Mega-CTA */}
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 text-center">
                <div className="mx-auto h-1.5 w-16 rounded-full mb-7" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }} />
                <p className="eyebrow-warm">{t("footer.ctaKicker", "Bereit?")}</p>
                <p className="font-heading mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-warm-strong leading-[1.02]">
                    {t("footer.ctaLine1", "Schreib deine Bewerbung")}
                    <br />
                    <span className="gradient-warm">{t("footer.ctaLine2", "in unter 60 Sekunden.")}</span>
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <Link to="/registrieren" data-testid="footer-cta-register" className="px-7 py-3.5 rounded-full font-semibold btn-primary-grad inline-flex items-center gap-2">
                        {t("footer.ctaPrimary", "Kostenlos starten")}
                    </Link>
                    <Link to="/partner" data-testid="footer-cta-partner" className="px-7 py-3.5 rounded-full font-semibold bg-white border-2 border-zinc-200 hover:border-warm-red text-warm-strong transition-colors inline-flex items-center gap-2">
                        <GraduationCap size={16} /> {t("nav.forPartners", "Für Partner")}
                    </Link>
                </div>
            </div>

            <div className="border-t border-zinc-200/60 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10">
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="b24-logo-shine"><img src="/logo-icon.png" alt="bewerbung.ai" className="h-8 w-auto object-contain" /></span>
                            <span className="font-heading font-bold tracking-tight"><span className="b24-shine-dark">Bewerbung24</span><span className="b24-shine-red">.ai</span></span>
                        </div>
                        <p className="text-sm text-warm leading-relaxed">{t("footer.tagline")}</p>
                    </div>
                    <div>
                        <h4 className="font-heading font-bold text-warm-red mb-4 text-xs uppercase tracking-[0.18em]">{t("nav.platform")}</h4>
                        <ul className="space-y-2 text-sm text-warm">
                            <li><Link to="/funktionen" className="hover:text-warm-red transition-colors">{t("nav.features")}</Link></li>
                            <li><Link to="/preise" className="hover:text-warm-red transition-colors">{t("nav.pricing")}</Link></li>
                            <li><Link to="/registrieren" className="hover:text-warm-red transition-colors">{t("nav.register")}</Link></li>
                            <li><Link to="/partner" className="hover:text-warm-red transition-colors font-semibold">{t("nav.forPartners", "Für Partner")}</Link></li>
                            <li><Link to="/unternehmen" data-testid="footer-company-link" className="hover:text-warm-red transition-colors font-semibold">{t("nav.forCompanies", "Für Unternehmen")}</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-heading font-bold text-warm-red mb-4 text-xs uppercase tracking-[0.18em]">Themen</h4>
                        <ul className="space-y-2 text-sm text-warm">
                            <li><Link to="/lebenslauf-erstellen" className="hover:text-warm-red transition-colors">Lebenslauf erstellen</Link></li>
                            <li><Link to="/bewerbung-erstellen" className="hover:text-warm-red transition-colors">Bewerbung erstellen</Link></li>
                            <li><Link to="/anschreiben-erstellen" className="hover:text-warm-red transition-colors">Anschreiben erstellen</Link></li>
                            <li><Link to="/ki-bewerbung" className="hover:text-warm-red transition-colors">KI-Bewerbung</Link></li>
                            <li><Link to="/bewerbungsfoto" className="hover:text-warm-red transition-colors">Bewerbungsfoto</Link></li>
                            <li><Link to="/ats-check" className="hover:text-warm-red transition-colors">ATS-Check</Link></li>
                            <li><Link to="/berufe" data-testid="footer-berufe-link" className="hover:text-warm-red transition-colors">Bewerbung nach Beruf</Link></li>
                            <li><Link to="/ratgeber" data-testid="footer-ratgeber-link" className="hover:text-warm-red transition-colors">Ratgeber</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-heading font-bold text-warm-red mb-4 text-xs uppercase tracking-[0.18em]">{t("nav.company")}</h4>
                        <ul className="space-y-2 text-sm text-warm">
                            <li><Link to="/ueber-uns" className="hover:text-warm-red transition-colors">{t("nav.about")}</Link></li>
                            <li><Link to="/kontakt" className="hover:text-warm-red transition-colors">{t("nav.contact")}</Link></li>
                            <li><Link to="/faq" className="hover:text-warm-red transition-colors">{t("nav.faq")}</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-heading font-bold text-warm-red mb-4 text-xs uppercase tracking-[0.18em]">{t("nav.legal")}</h4>
                        <ul className="space-y-2 text-sm text-warm">
                            <li><Link to="/datenschutz" className="hover:text-warm-red transition-colors">{t("nav.privacy")}</Link></li>
                            <li><Link to="/impressum" className="hover:text-warm-red transition-colors">{t("nav.imprint")}</Link></li>
                            <li><Link to="/agb" data-testid="footer-terms-link" className="hover:text-warm-red transition-colors">AGB</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-zinc-200/60">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 text-sm text-warm flex flex-col sm:flex-row justify-between items-center gap-2">
                        <p>© {new Date().getFullYear()} Bewerbung24.ai. {t("footer.rights")}</p>
                        <p>{t("footer.compliance")}</p>
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default function PublicLayout({ children }) {
    return (
        <div className="min-h-screen flex flex-col bg-white">
            <PublicHeader />
            <main className="flex-1">{children}</main>
            <PublicFooter />
        </div>
    );
}
